import type { OpaqueVideoData, Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import { cloneDeep, omit } from 'lodash';

import { getLinkedMediaAssets } from 'js/src/libs/scene-utils';
import { getSceneDesignConfig } from 'js/src/luminary/scene-design-config';

type RawNode = Record<string, unknown>;

type RawSceneData = RawNode & {
    children?: RawNode[];
    subslides?: { children?: RawNode[] }[];
};

function isRawNode(value: unknown): value is RawNode {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

function collectDesignAssets(nodes: unknown, collected: RawNode[] = []): RawNode[] {
    if (!Array.isArray(nodes)) {
        return collected;
    }
    nodes.filter(isRawNode).forEach((node) => {
        collected.push(node);
        collectDesignAssets(node.children, collected);
    });
    return collected;
}

/**
 * A scene can hold a speaker its own design has no slot for: add_voiceover_scene copies a talking
 * head's base scene onto the template's cutaway design, so the copy keeps the base design's linked
 * `media_1` while the cutaway's only media slot is a stock `media_2`. Luminary rebuilds a scene from
 * its design on an aspect ratio change and re-applies edits by asset name, so that speaker is lost
 * and the design's stock media shows instead.
 *
 * Rebuilds such a scene from its own design with the speaker in the design's media slot — what the
 * editor builds for a media-only overlay — so a rebuild finds it. The design's placeholder text is
 * blanked, and the scene's own edits to slots the design shares by name are re-applied, as Luminary
 * does on an aspect ratio change. Scenes holding anything else the design doesn't have are left
 * alone.
 */
export function seatSpeakerInDesignMediaSlot(scene: Scene): void {
    const designConfig = getSceneDesignConfig(scene);
    if (!isRawNode(designConfig)) {
        return;
    }
    const designData = designConfig as RawSceneData;
    const designAssets = collectDesignAssets(designData.children);
    const designAssetNames = new Set(
        [
            ...designAssets,
            ...(designData.subslides ?? []).flatMap((subslide) =>
                collectDesignAssets(subslide.children),
            ),
        ].map((node) => node.name),
    );

    const speakers = getLinkedMediaAssets(scene).filter(
        (asset) => !designAssetNames.has(asset.name),
    );
    if (speakers.length !== 1) {
        return;
    }
    const [speaker] = speakers;

    const holdsOtherContent = scene
        .getAssets()
        .some(
            (asset) =>
                asset !== speaker &&
                !VideoData.isStickerAsset(asset) &&
                !designAssetNames.has(asset.name),
        );
    if (holdsOtherContent) {
        return;
    }

    const designMediaSlotNames = designAssets
        .filter((node) => node.classType === VideoData.MEDIA_ASSET_TYPE && node.isEditable)
        .map((node) => node.name);
    if (designMediaSlotNames.length !== 1) {
        return;
    }

    const rebuilt = cloneDeep(designData);
    const mediaSlot = collectDesignAssets(rebuilt.children).find(
        (node) => node.name === designMediaSlotNames[0],
    )!;
    Object.assign(mediaSlot, omit(speaker.save() as RawNode, ['id', 'name', 'parentId']));

    const current = scene.save() as RawSceneData;
    const edits = scene.saveEditable();
    const { duration, sceneCategory } = scene;
    rebuilt.id = current.id;
    rebuilt.sceneGroupId = current.sceneGroupId;
    rebuilt.stickers = current.stickers;
    rebuilt.customTransition = current.customTransition;
    rebuilt.transitionEnabled = current.transitionEnabled;

    scene.clear();
    scene.load(rebuilt as OpaqueVideoData);
    scene.generateNewAssetIds();
    scene.duration = duration;
    if (sceneCategory) {
        scene.sceneCategory = sceneCategory;
    }
    scene.getAssetsByType(VideoData.TEXT_ASSET_TYPE).forEach((text) => {
        text.text = '';
    });
    scene.loadEditable(edits);
}
